/**
 * An outfit as the member sees it: its garments laid out on one card, the
 * "spread" the consumer app draws (apps/consumer/src/wardrobe/components/
 * OutfitGrid.tsx `OutfitArtwork`). Admin used to list an outfit's garments
 * inline - a row or grid of separate tiles - which reads as a pile of pieces
 * rather than one look; every place admin shows an outfit uses this instead.
 *
 * The placements are the consumer's, copied verbatim, so a look composes the
 * same here as on the member's phone: garments take fixed slots by position,
 * up to five, three slots for three or fewer. A garment known only by id is
 * resolved to its image through the shared reference batch.
 */

import type { ReactNode } from 'react';
import { GarmentImage } from './garment-image';
import { useReference } from './references';

/** GarmentImage without its tile frame: a cut-out sitting straight on the canvas. */
const BARE = 'aspect-auto h-full rounded-none border-0 bg-transparent';

const PLACEMENTS_UP_TO_3 = [
  { left: 28, top: 3, width: 64, height: 54 },
  { left: 3, top: 39, width: 50, height: 45 },
  { left: 43, top: 47, width: 55, height: 48 },
] as const;
const PLACEMENTS_UP_TO_5 = [
  { left: 28, top: 1, width: 64, height: 49 },
  { left: 1, top: 35, width: 45, height: 39 },
  { left: 49, top: 39, width: 49, height: 42 },
  { left: 4, top: 67, width: 42, height: 31 },
  { left: 48, top: 69, width: 48, height: 29 },
] as const;

/** The consumer's card colours: `canvasAlt` behind, a `sage` hairline. */
const CANVAS = '#fbfcf7';
const SAGE = '#e7f1d6';

export const SPREAD_MAX_GARMENTS = 5;

export type SpreadGarment = {
  id: string;
  /** A signed image URL; omit it to resolve the garment by id. */
  imageUrl?: string | null;
  /** The garment's name, for the image's alt text. */
  label?: string | null;
};

function SpreadGarmentImage({
  garment,
  memberId,
  style,
}: {
  garment: SpreadGarment;
  memberId?: string | undefined;
  style: React.CSSProperties;
}) {
  // `undefined` means the screen only knows the id; `null` is a record saying there is no
  // image, which a lookup would only confirm.
  const known = garment.imageUrl !== undefined;
  const entry = useReference(
    { kind: 'garment', id: garment.id, ...(memberId ? { memberId } : {}) },
    known ? (garment.label ?? garment.id) : null,
  );
  const resolved = entry.state === 'ready' ? entry.value : undefined;
  const url = known ? garment.imageUrl : resolved?.imageUrl;
  const label = garment.label ?? resolved?.label ?? 'Garment';
  return (
    <div className="absolute" style={style} data-spread-garment={garment.id}>
      {url ? (
        <GarmentImage url={url} refPresent={garment.id} alt={label} className={BARE} />
      ) : (
        <div
          role="img"
          aria-label={entry.state === 'loading' ? `Loading ${label}` : `${label}: no image`}
          className={`h-full w-full rounded-lg ${entry.state === 'loading' ? 'animate-pulse bg-secondary' : 'border border-dashed border-secondary'}`}
        />
      )}
    </div>
  );
}

/**
 * The canvas alone: garments in their slots, or the outfit's render when it has
 * no garments to lay out. Width comes from the container; the card is 3:4.
 */
export function OutfitSpread({
  garments,
  renderUrl,
  memberId,
  label = 'Outfit',
  className = '',
}: {
  garments: readonly SpreadGarment[];
  /** The try-on render, shown only when there are no garments to lay out. */
  renderUrl?: string | null;
  memberId?: string | undefined;
  label?: string;
  className?: string;
}) {
  const visible = garments.slice(0, SPREAD_MAX_GARMENTS);
  const placements = visible.length <= 3 ? PLACEMENTS_UP_TO_3 : PLACEMENTS_UP_TO_5;
  const hidden = garments.length - visible.length;
  return (
    <div
      role="group"
      aria-label={`${label}: ${garments.length} ${garments.length === 1 ? 'garment' : 'garments'}`}
      className={`relative aspect-[3/4] w-full overflow-hidden rounded-xl ${className}`}
      style={{
        background: CANVAS,
        border: `1px solid ${SAGE}`,
        boxShadow: 'inset 0 0 24px rgba(231,241,214,0.5)',
      }}
    >
      {visible.length ? (
        visible.map((garment, index) => {
          const placement = placements[index];
          if (!placement) return null;
          return (
            <SpreadGarmentImage
              key={`${garment.id}-${index}`}
              garment={garment}
              memberId={memberId}
              style={{
                left: `${placement.left}%`,
                top: `${placement.top}%`,
                width: `${placement.width}%`,
                height: `${placement.height}%`,
              }}
            />
          );
        })
      ) : renderUrl ? (
        <GarmentImage url={renderUrl} refPresent={renderUrl} alt={label} className={BARE} />
      ) : (
        <div className="flex h-full items-center justify-center p-4 text-center text-xs text-tertiary">
          No garments on this outfit.
        </div>
      )}
      {hidden > 0 ? (
        <span className="absolute right-2 bottom-2 rounded-full bg-primary px-2 py-0.5 text-xs font-medium text-secondary shadow-xs">
          +{hidden} more
        </span>
      ) : null}
    </div>
  );
}

/** The canvas with a caption beneath: a title, then anything the screen adds. */
export function OutfitSpreadCard({
  garments,
  renderUrl,
  memberId,
  title,
  badge,
  children,
  className = '',
}: {
  garments: readonly SpreadGarment[];
  renderUrl?: string | null;
  memberId?: string | undefined;
  title?: ReactNode;
  /** A short status shown over the canvas's top-left corner. */
  badge?: ReactNode;
  children?: ReactNode;
  className?: string;
}) {
  return (
    <figure className={`flex min-w-0 flex-col gap-2 ${className}`}>
      <div className="relative">
        <OutfitSpread
          garments={garments}
          renderUrl={renderUrl}
          memberId={memberId}
          label={typeof title === 'string' ? title : 'Outfit'}
        />
        {badge ? <div className="absolute top-2 left-2">{badge}</div> : null}
      </div>
      {title || children ? (
        <figcaption className="flex min-w-0 flex-col gap-1 text-sm">
          {title ? <span className="font-medium text-primary">{title}</span> : null}
          {children}
        </figcaption>
      ) : null}
    </figure>
  );
}
